import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

import {
  useGetReceivingsQuery,
} from '#services/receiving';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
  toDateRangeQueryParams,
} from '#components/time-range.utils';
import DropdownFilterBox from '#components/DropdownFilterBox';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  formatDate,
} from '#common/utils';
import URLSearchParamsEx from '#utils/url-ex';
import {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setSelected,
  setSkipFlag,
} from './receiving.slice';

interface ReceivingRow {
  id: string | number;
  createdBy?: string;
  createdDate?: string;
  vendors?: string[];
  items: unknown[];
  [key: string]: unknown;
}

interface ReceivingListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  selected: Array<string | number | undefined>;
  vendors: Record<string, React.ReactNode>;
  skip: boolean;
}

export default function ReceivingList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const filter = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.filter);
  const dateRange = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.dateRange);
  const filterKey = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.filterKey);
  const headerFilters = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.headerFilters);
  const order = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.order);
  const orderBy = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.orderBy);
  const pageIndex = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.pageIndex);
  const pageSize = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.pageSize);
  const rows = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.rows);
  const rowCount = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.rowCount);
  const selected = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.selected);
  const vendors = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.vendors);
  const skip = useSelector((state: { receivingList: ReceivingListSlice }) => state.receivingList.skip);
  const [filterForQuery, setFilterForQuery] = useState(filter);

  const {
    showModal,
    hideModal,
    setModalData,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const [isInitialMount, setIsInitialMount] = useState(true);

  const dateRangeParam = resolveDateRangeParam('createdDate', dateRange);

  const query = useGetReceivingsQuery({
    dateRange: dateRangeParam,
    filter: filterForQuery,
    filterKey,
    headerFilters,
    order,
    orderBy,
    pageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
    skip,
  });

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.filterKey !== undefined) {
      dispatch(setFilterKey(urlParams.values.filterKey as string));
    }
    if (urlParams.values.headerFilters !== undefined) {
      dispatch(setHeaderFilters(urlParams.values.headerFilters as Record<string, unknown>));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as unknown as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  useEffect(() => {
    // When filterForQuery changes, always trigger a search by setting skip to false
    dispatch(setSkipFlag(false));
  }, [filterForQuery, dispatch]);

  const handleOrdersExport = () => {
    // WORKAROUND react state cannot work, use local variable instead
    let exportTargetOption = 'current';

    setModalData({
      children: (
        <>
          <Typography variant="h6" gutterBottom>
            Export Orders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControl sx={{ mb: 5 }}>
            <FormLabel id="label-export-radio">Export</FormLabel>
            <RadioGroup
              aria-labelledby="label-export-radio"
              defaultValue="current"
              name="radio-buttons-group"
              onChange={(event, value) => {
                exportTargetOption = value;
              }}
            >
              <FormControlLabel value="current" control={<Radio />} label="Current page" />
              <FormControlLabel value="all" control={<Radio />} label="All receiving list" />
              <FormControlLabel
                disabled={!selected.length}
                value="selected"
                control={<Radio />}
                label={`Selected: ${selected.length} receiving list`}
              />
            </RadioGroup>
          </FormControl>

          <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                let additionalParam = '';
                if (exportTargetOption === 'current') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&pageIndex=${pageIndex}&pageSize=${pageSize}`;
                } else if (exportTargetOption === 'all') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&all=1`;
                } else if (exportTargetOption === 'selected') {
                  additionalParam = `receivingIds=${selected.join(',')}`;
                }

                // An explicit selection is already the exact row set the user
                // picked, so only the page/all exports carry the table's range.
                if (exportTargetOption !== 'selected') {
                  const rangeParams = new URLSearchParams(
                    toDateRangeQueryParams(dateRangeParam),
                  ).toString();
                  if (rangeParams) {
                    additionalParam = `${additionalParam}&${rangeParams}`;
                  }
                }

                const exportUrl = `/api/export/receivings?${additionalParam}`;
                window.open(exportUrl, '_blank');
                hideModal();
              }}
            >
              Export orders
            </Button>
          </Stack>
        </>
      ),
      style: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
      },
    });
    showModal();
  };

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'id',
    label: 'Receiving #',
    renderer: (row, idx) => {
      const r = row as unknown as ReceivingRow;
      const receivingIndex = pageIndex * pageSize + idx + 1;
      const qs = new URLSearchParamsEx({
        filter,
        headerFilters,
        order,
        orderBy,
        pageSize,
        receivingId: r.id,
        receivingIndex,
        // The detail page re-runs the list query to resolve the row index (and
        // to walk prev/next), so it needs the same date window the list used —
        // without it the index points into the unfiltered list.
        ...(dateRangeParam ? { dateRange: dateRangeParam } : {}),
      });
      const url = `/management/receiving/edit?${qs.toString()}`;
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
        >
          {r.id}
        </Link>
      );
    },
  }, {
    id: 'createdBy',
    label: 'Created By',
    renderer: (r) => (r as unknown as ReceivingRow).createdBy || '-',
  }, {
    id: 'createdDate',
    label: 'Create Date',
    renderer: (r) => formatDate((r as unknown as ReceivingRow).createdDate as string, '-'),
  }, {
    id: 'vendor',
    label: 'Vendor',
    filters: vendors,
    renderer: (row) => {
      const r = row as unknown as ReceivingRow;
      return (r.vendors && r.vendors.length)
        ? r.vendors.join(', ')
        : '-';
    },
  }, {
    id: 'items',
    label: 'Items',
    renderer: (row) => {
      const r = row as unknown as ReceivingRow;
      return r.items.length;
    },
  }];

  const tableProps: EnhancedTableProps = {
    headers: tableHeaders,
    pinnedColumns: {
      left: ['id'],
    },
    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,

    hasSelectColumn: true,
    selected,
    setSelected,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Create Date',
      defaultPreset: DEFAULT_TIME_RANGE_PRESET,
      entityLabel: 'receivings',
    },

    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Receiving List
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <DropdownFilterBox
          width={500}
          dropdownItems={[{
            value: 'id',
            text: 'Receiving #',
          }, {
            value: 'barcode',
            text: 'Barcode #',
          }]}
          filter={filter}
          filterKey={filterKey}
          filterPlaceholder="Filter receiving list"
          dispatch={dispatch as (action: unknown) => void}
          setFilter={setFilter}
          setFilterKey={setFilterKey}
          setFilterForQuery={setFilterForQuery}
        />

        <ButtonGroup variant="outlined" size="small">
          <Button
            // disabled={!userInfo.isAdmin || !selected.length}
            onClick={() => { void navigate('/management/receiving/edit'); }}
          >
            Create New Receiving List
          </Button>

          <Button
            onClick={handleOrdersExport}
          >
            Export
          </Button>
        </ButtonGroup>
      </Box>

      <EnhancedTable
        enableSort
        name="variants"
        {...tableProps}
      />
    </>
  );
}
